import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { vaultState, setupPin, unlockWithPin, isPinConfigured } from '../../state/security/vault.js';
import { hydrateStores } from '../../state/security/hydrateStores.js';

type Phase = 'enter' | 'confirm';

const COOLDOWN_SECONDS = 10;
const MAX_ATTEMPTS = 6;

function Spinner() {
  return (
    <div
      style={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        border: '2px solid rgba(198,184,112,0.2)',
        borderTopColor: '#c6b870',
        animation: 'vg-spin 0.8s linear infinite',
      }}
    />
  );
}

function DotRow({ filled, shake }: { filled: number; shake: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 20,
        alignItems: 'center',
        animation: shake ? 'vg-shake 0.38s ease' : undefined,
      }}
    >
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          style={{
            width: 14,
            height: 14,
            borderRadius: '50%',
            background: i < filled ? '#c6b870' : 'transparent',
            border: '2px solid',
            borderColor: i < filled ? '#c6b870' : 'rgba(198,184,112,0.4)',
            transition: 'background 0.1s, border-color 0.1s',
          }}
        />
      ))}
    </div>
  );
}

function NumKey({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: 72,
        height: 72,
        borderRadius: '50%',
        border: '1px solid rgba(198,184,112,0.18)',
        background: 'rgba(255,255,255,0.04)',
        color: '#e8e0cc',
        fontSize: 22,
        fontWeight: 400,
        fontFamily: 'ui-sans-serif, system-ui, sans-serif',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        WebkitTapHighlightColor: 'transparent',
        transition: 'background 0.1s',
        lineHeight: 1,
      }}
      onMouseDown={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = 'rgba(198,184,112,0.12)';
      }}
      onMouseUp={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.04)';
      }}
      onTouchStart={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = 'rgba(198,184,112,0.12)';
      }}
      onTouchEnd={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.04)';
      }}
    >
      {label}
    </button>
  );
}

function NumPad({
  onDigit,
  onBack,
  disabled,
}: {
  onDigit: (d: string) => void;
  onBack: () => void;
  disabled: boolean;
}) {
  const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'];
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 72px)',
        gap: 14,
        marginTop: 32,
        opacity: disabled ? 0.35 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      {keys.map((k, i) =>
        k === '' ? (
          <div key={i} />
        ) : k === '⌫' ? (
          <NumKey key={i} label="⌫" onClick={onBack} />
        ) : (
          <NumKey key={i} label={k} onClick={() => onDigit(k)} />
        ),
      )}
    </div>
  );
}

function CooldownBar({ remaining }: { remaining: number }) {
  return (
    <div
      style={{
        width: 220,
        height: 3,
        borderRadius: 999,
        background: 'rgba(198,184,112,0.15)',
        overflow: 'hidden',
        marginTop: 24,
      }}
    >
      <div
        style={{
          width: `${(remaining / COOLDOWN_SECONDS) * 100}%`,
          height: '100%',
          background: '#c6b870',
          transition: 'width 1s linear',
        }}
      />
    </div>
  );
}

export function VaultGate() {
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [phase, setPhase] = useState<Phase>('enter');
  const [shake, setShake] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [cooldown, setCooldown] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const state = vaultState.value;
  const settingUp = state === 'uninitialized';

  useEffect(() => {
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    setIsDesktop(mq.matches);
    const handler = (e: MediaQueryListEvent): void => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const currentPin = phase === 'confirm' ? confirmPin : pin;
  const setCurrentPin = phase === 'confirm' ? setConfirmPin : setPin;

  const triggerShake = useCallback(() => {
    setShake(true);
    setTimeout(() => setShake(false), 400);
  }, []);

  const startCooldown = useCallback(() => {
    setCooldown(COOLDOWN_SECONDS);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) {
          clearInterval(cooldownRef.current!);
          cooldownRef.current = null;
          setAttempts(0);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  const handleDigit = useCallback(
    (d: string) => {
      if (processing || cooldown > 0) return;
      const next = currentPin + d;
      setCurrentPin(next);

      if (next.length === 4) {
        void (async () => {
          setProcessing(true);
          if (settingUp) {
            if (phase === 'enter') {
              setPhase('confirm');
              setProcessing(false);
            } else {
              if (next === pin) {
                await setupPin(pin);
                await hydrateStores();
              } else {
                triggerShake();
                setConfirmPin('');
                setPhase('enter');
                setPin('');
                setProcessing(false);
              }
            }
          } else {
            const ok = await unlockWithPin(next);
            if (ok) {
              await hydrateStores();
            } else {
              const newAttempts = attempts + 1;
              setAttempts(newAttempts);
              triggerShake();
              setPin('');
              setProcessing(false);
              if (newAttempts >= MAX_ATTEMPTS) {
                startCooldown();
              }
            }
          }
        })();
      }
    },
    [
      processing,
      cooldown,
      currentPin,
      setCurrentPin,
      settingUp,
      phase,
      pin,
      attempts,
      triggerShake,
      startCooldown,
    ],
  );

  const handleBack = useCallback(() => {
    if (processing || cooldown > 0) return;
    setCurrentPin((p) => p.slice(0, -1));
  }, [processing, cooldown, setCurrentPin]);

  useEffect(() => {
    if (!isDesktop) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key >= '0' && e.key <= '9') handleDigit(e.key);
      else if (e.key === 'Backspace') handleBack();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isDesktop, handleDigit, handleBack]);

  const showSpinner = state === 'uninitialized' && !settingUp;

  return (
    <>
      <style>{`
        @keyframes vg-spin {
          to { transform: rotate(360deg); }
        }
        @keyframes vg-shake {
          0%   { transform: translateX(0); }
          15%  { transform: translateX(-8px); }
          35%  { transform: translateX(8px); }
          55%  { transform: translateX(-6px); }
          75%  { transform: translateX(6px); }
          90%  { transform: translateX(-3px); }
          100% { transform: translateX(0); }
        }
      `}</style>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: '#0a0a0a',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          userSelect: 'none',
        }}
      >
        {showSpinner ? (
          <Spinner />
        ) : (
          <>
            {settingUp && (
              <p
                style={{
                  margin: '0 0 28px',
                  color: 'rgba(198,184,112,0.55)',
                  fontSize: 13,
                  fontFamily: 'ui-sans-serif, system-ui, sans-serif',
                  letterSpacing: '0.04em',
                }}
              >
                {phase === 'confirm' ? 'Confirm code' : 'Choose a 4-digit code'}
              </p>
            )}

            <DotRow filled={currentPin.length} shake={shake} />

            {cooldown > 0 && <CooldownBar remaining={cooldown} />}

            {!isDesktop && (
              <NumPad
                onDigit={handleDigit}
                onBack={handleBack}
                disabled={processing || cooldown > 0}
              />
            )}

            {isDesktop && isPinConfigured() && (
              <p
                style={{
                  marginTop: 32,
                  color: 'rgba(198,184,112,0.3)',
                  fontSize: 12,
                  fontFamily: 'ui-sans-serif, system-ui, sans-serif',
                  letterSpacing: '0.04em',
                }}
              >
                Type your 4-digit code
              </p>
            )}
          </>
        )}
      </div>
    </>
  );
}
